<script setup>

import { computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useWebsiteStore } from "@/stores/website";
import { storeToRefs } from "pinia";

const props = defineProps({
  history: {type: Object, default: []},
  label: {type: String, default: 'Visual Changes'},
  query: {type: String, default: ''},
  to: {type: Object, default: null},
  showPrompts: {type: Boolean, default: false}
})
const websiteStore = useWebsiteStore();
const {website} = storeToRefs(websiteStore);
const router = useRouter()
const route = useRoute()

const entries = computed(() => {
  // Compute dynamic range based on values in history
  const values = (props.history || []).map(e => (e.new || 0) + (e.text || 0) + (e.difference || 0))
  const max = values.length ? Math.max(...values) : 0

  // Color helpers: interpolate from white to dark blue
  const from = { r: 255, g: 255, b: 255 } // white (no changes)
  const to = { r: 13, g: 71, b: 161 }     // dark blue (#0d47a1, many changes)
  const clamp01 = (x) => Math.max(0, Math.min(1, x))
  const lerp = (a, b, t) => Math.round(a + (b - a) * t)
  const toHex = (n) => n.toString(16).padStart(2, '0')
  const mix = (t) => {
    const r = lerp(from.r, to.r, t)
    const g = lerp(from.g, to.g, t)
    const b = lerp(from.b, to.b, t)
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`
  }

  let history = [...(props.history || [])]
  const minEntries = 3
  const paddingNeeded = Math.max(0, minEntries - history.length)
  
  const mappedEntries = history.map((entry, index) => {
    const value = (entry.new || 0) + (entry.text || 0) + (entry.difference || 0)
    const t = max > 0 ? clamp01(value / max) : 0
    const color = mix(t)

    let date = new Date(entry.date)
    const dateFull = date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true
    })

    const dateShort = date.toLocaleDateString('en-us', {
      month: "short",
      day: "numeric",
    })

    // Extract real data for prompt statuses from tracked_prompts
    const tracked = entry.trackedPrompts || {}

    const prompts = {
      answered_and_verified:(tracked.updated?.answered_and_verified || []).length,
      llm_not_answered: (tracked.updated?.llm_not_answered || []).length,
      llm_not_verified: (tracked.updated?.llm_not_verified || []).length,
      not_answered: (tracked.updated?.not_answered || []).length,
      updated_answer: (tracked.updated?.updated_answer || []).length,
      llm_updated_answer: (tracked.updated?.llm_updated_answer || []).length,
    }
    const totalPrompts = prompts.answered_and_verified + prompts.llm_not_answered + prompts.llm_not_verified + prompts.not_answered + prompts.updated_answer + prompts.llm_updated_answer

    const visualText = !entry.isPending ? `${value} Changes Detected` : ''

    const promptSegments = []
    if (totalPrompts > 0) {
      if (prompts.answered_and_verified > 0) promptSegments.push({ label: `${prompts.answered_and_verified} Question${prompts.answered_and_verified > 1 ? 's': ''} became verified`, color: '#4caf50', width: (prompts.answered_and_verified / totalPrompts) * 100 })
      if (prompts.updated_answer > 0) promptSegments.push({ label: `${prompts.updated_answer} Answer${prompts.updated_answer > 1 ? 's': ''} changed`, color: '#2196f3', width: (prompts.updated_answer / totalPrompts) * 100 })
      if (prompts.llm_updated_answer > 0) promptSegments.push({ label: `${prompts.llm_updated_answer} LLM Answer${prompts.llm_updated_answer > 1 ? 's': ''} changed`, color: '#21e1f3', width: (prompts.updated_answer / totalPrompts) * 100 })
      if (prompts.llm_not_verified > 0) promptSegments.push({ label: `AI failed to answer ${prompts.llm_not_verified} question${prompts.llm_not_verified > 1 ? 's': ''} correctly`, color: '#ff9800', width: (prompts.llm_not_verified / totalPrompts) * 100 })
      if (prompts.llm_not_answered > 0) promptSegments.push({ label: `AI could not answer ${prompts.llm_not_answered} question${prompts.llm_not_answered > 1 ? 's': ''}`, color: '#ffb74d', width: (prompts.llm_not_answered / totalPrompts) * 100 })
      if (prompts.not_answered > 0) promptSegments.push({ label: `${prompts.not_answered} Question${prompts.not_answered > 1 ? 's': ''} is no longer answered`, color: '#f44336', width: (prompts.not_answered / totalPrompts) * 100 })
    }

    return {
      ...entry,
      value,
      color,
      promptSegments,
      dateFull,
      dateShort,
      prompts,
      visualText,
      hasUpdates: totalPrompts > 0,
      hasPrompts: totalPrompts > 0,
      index,
      isPending: false
    }
  })

  // Add pending entries if needed
  for (let i = 0; i < paddingNeeded; i++) {
    mappedEntries.push({
      isPending: true,
      color: '#f8f9fa', // Very light gray
      dateFull: 'Pending Scan',
      dateShort: '-',
      value: 0,
      prompts: { answered_and_verified: 0, llm_not_answered: 0, llm_not_verified: 0, not_answered: 0, updated: 0 },
      promptSegments: [],
      hasPrompts: false,
      visualText: 'Scheduled Scan',
      hasUpdates: false,
      index: mappedEntries.length
    })
  }

  return mappedEntries
})

function selectPoint(entry, index) {
  if (entry.isPending) return;

  let query = {};
  query.history = props.history.length - index;
  query.section = 'history';

  if (route.params.deviceId) {
    router.push({query: query, params: route.params});
  }

  if (props.to) {
    let to = {}
    Object.assign(to, props.to);
    to.query.history = props.history.length - index;
    router.push(to);
  }
}

defineExpose({});
</script>

<template>
<div class="heatmap-container" >
  <div class="heatmap-row">
    <div class="heatmap-row-label" v-if="label">{{ label }}</div>
    <div class="heatmap-grid">
      <div 
        v-for="(entry, index) in entries" 
        :key="index"
        class="heatmap-box"
        :class="{ 'is-pending': entry.isPending }"
        :style="{ backgroundColor: entry.color }"
        @click="selectPoint(entry, index)"
        v-tooltip=entry.visualText
      >
      </div>
    </div>
  </div>
  <div class="heatmap-row" v-if="showPrompts">
    <div class="heatmap-row-label">Updated Answers</div>
    <div class="heatmap-grid">
      <div 
        v-for="(entry, index) in entries" 
        :key="'prompts-' + index"
        class="heatmap-box proportional"
        :class="{ 'is-pending': entry.isPending }"
        @click="selectPoint(entry, index)"
      >
        <template v-if="entry.promptSegments && entry.promptSegments.length">
          <div 
            v-for="(seg, sIdx) in entry.promptSegments" 
            :key="sIdx"
            class="heatmap-segment"
            :style="{ backgroundColor: seg.color, width: seg.width + '%' }"
            v-tooltip="seg.label"
          ></div>
        </template>
        <div v-else-if="!entry.isPending" class="heatmap-segment" style="background-color: #f0f0f0; width: 100%" v-tooltip="'No Updates'"></div>
        <div v-else class="heatmap-segment" style="width: 100%" v-tooltip="'Scheduled Scan'"></div>
      </div>
    </div>
  </div>
  <div class="heatmap-labels" :class="{'with-labels': label}">
    <div v-for="(entry, index) in entries" :key="'label-' + index" class="heatmap-label">
      {{ entry.dateShort }}
    </div>
  </div>
</div>

</template>

<style scoped lang="scss">
.heatmap-container {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 2px;
  
  &.with-legend {
    min-height: 80px;
  }
  
  &:not(.with-legend) {
    min-height: 30px;
  }
}

.heatmap-legend {
  font-size: 10px;
  .prompt-dot {
    width: 6px;
    height: 6px;
  }
}

.heatmap-row {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
}

.heatmap-row-label {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  color: #888;
  width: 100px;
  flex-shrink: 0;
  text-align: left;
  white-space: nowrap;
}

.heatmap-grid {
  display: flex;
  flex: 1;
  height: 14px;
}


.heatmap-box {
  flex: 1;
  height: 100%;
  cursor: pointer;
  border-right: 1px solid rgba(0,0,0,0.05);
  border-top: 1px solid rgba(0,0,0,0.05);
  border-bottom: 1px solid rgba(0,0,0,0.05);
  transition: filter 0.2s;

  &.is-pending {
    cursor: default;
    border-color: #eee;
    background-image: repeating-linear-gradient(
      45deg,
      transparent,
      transparent 5px,
      rgba(0,0,0,0.02) 5px,
      rgba(0,0,0,0.02) 10px
    );
  }

  &:first-child {
    border-left: 1px solid rgba(0,0,0,0.05);
    &.is-pending {
      border-left: 1px solid #eee;
    }
  }

  &:hover:not(.is-pending) {
    filter: brightness(0.85);
  }

  &.proportional {
    display: flex;
    overflow: hidden;
  }
}

.heatmap-segment {
  height: 100%;
  flex-shrink: 0;
}

.heatmap-labels {
  display: flex;
  width: 100%;
  padding-left: 0; /* Offset for row labels when legend is false (100px width + 8px gap) */
  font-size: 10px;
  color: #666;
  overflow: hidden;
}

.with-labels.heatmap-labels {
  padding-left: 108px; /* Offset for row labels when legend is false (100px width + 8px gap) */
}

.heatmap-label {
  flex: 1;
  text-align: center;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
}
</style>